import React, {useState} from 'react';

import {Button, Heading} from '@albert-io/atomic';
import EmptyStateCta from 'client/Classrooms/Teacher/EmptyStateCta/EmptyStateCta.react';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import sessionStore from 'client/Session/SessionStore';
import {AdaptiveSubjectCourseCard} from 'client/SubjectLibrary/AdaptiveSubjectCourseCard/AdaptiveSubjectCourseCard';
import {SubjectsFilterMenu} from 'client/TeacherDashboard/ClassDetail/Subjects/SubjectsFilterMenu';
import SubjectCourseCard from 'client/generic/SubjectMenuRenderer/SubjectCourseCard/SubjectCourseCard';
import type {SubjectCardData} from 'client/generic/SubjectMenuRenderer/types';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {useClassworkSubjects} from './useClassworkSubjects';

// v2 all-classwork Subjects view: the subjects used across every class, each with the course-library
// Practice Settings dropdown. A Flux-free port of SubjectLibrary/ClassSubjects, rendered in the v2
// classwork chrome instead of the legacy SubjectMenuRenderer.
export const ClassworkSubjects = () => {
  const {
    isReady,
    hasNoClassrooms,
    subjects,
    activeClassrooms,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    isUserLicensedTeacher
  } = useClassworkSubjects();
  const isLoggedOutOrStudent = !sessionStore.hasValidSession() || sessionStore.isStudent();
  const [isNewClassModalOpen, setIsNewClassModalOpen] = useState(false);

  if (!isReady) {
    return <LoadingIndicator />;
  }

  if (hasNoClassrooms) {
    return (
      <>
        <EmptyStateCta>
          <EmptyStateCta.Heading>No class subjects</EmptyStateCta.Heading>
          <EmptyStateCta.Text>
            Subjects that are used in your classrooms will appear here. To get started, create a
            class.
          </EmptyStateCta.Text>
          <Button className='mb-x2' onClick={() => setIsNewClassModalOpen(true)}>
            Create a class
          </Button>
        </EmptyStateCta>
        {isNewClassModalOpen && (
          <NewClassroomModal
            handleClose={() => setIsNewClassModalOpen(false)}
            onSubmit={() => setIsNewClassModalOpen(false)}
          />
        )}
      </>
    );
  }

  if (subjects.length === 0) {
    return (
      <EmptyStateCta>
        <EmptyStateCta.Heading>No class subjects</EmptyStateCta.Heading>
        <EmptyStateCta.Text>
          To get started, create an assignment or open subjects to allow independent practice for
          your students.
        </EmptyStateCta.Text>
        <EmptyStateCta.Anchor href='https://help.albert.io/en/articles/3816818-allow-students-to-independently-practice-a-subject'>
          Learn more about independent practice
        </EmptyStateCta.Anchor>
      </EmptyStateCta>
    );
  }

  return (
    <div className='flex flex-col gap-x3 pt-x4'>
      <Heading as='h2' className='m-x0' size='s'>
        Subjects
      </Heading>
      {hasHiddenSubjects && (
        <div className='flex'>
          <SubjectsFilterMenu showHidden={showHidden} onToggle={setShowHidden} />
        </div>
      )}
      <div className='grid grid-cols-1 gap-x3 s:grid-cols-2 m:grid-cols-3'>
        {subjects.map((subject) => {
          if (subject.isAdaptive()) {
            return (
              <AdaptiveSubjectCourseCard
                key={subject.getId()}
                subjectId={subject.getId()}
                subjectName={subject.getName()}
                subjectUrlSlug={subject.getUrlSlug()}
                subjectDescription={subject.getDescription() || ''}
                curriculumAreaName={subject.getCurriculumArea().getName() || ''}
                isLoggedOutOrStudent={isLoggedOutOrStudent}
                data-testid={subject.getName()}
              />
            );
          }
          const cardData: SubjectCardData = {
            id: subject.getId(),
            name: subject.getName(),
            description: subject.getDescription(),
            urlSlug: subject.getUrlSlug(),
            curriculumAreaSlug: subject.getCurriculumAreaSlug(),
            logoHref: subject.getLogoLocation().href
          };
          return (
            <SubjectCourseCard
              key={subject.getId()}
              subject={cardData}
              to={`/${subject.getUrlSlug()}`}
              content={subject.getDescription()}
              isAssessmentsOnly={subject
                .getGuides()
                .every((guide) => guide.getGuideType() === 'assessment')}
              isUserLicensedTeacher={isUserLicensedTeacher}
              isLoggedOutOrStudent={isLoggedOutOrStudent}
              activeClassrooms={activeClassrooms}
            />
          );
        })}
      </div>
    </div>
  );
};
